/* eslint-disable class-methods-use-this */
/* eslint-disable consistent-return */
import auth0 from 'auth0-js';
import EventEmitter from 'events';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import debounce from 'lodash/debounce';
import jwt_decode from 'jwt-decode';

import authConfig from './auth.json';
import getActiveOrg from '../utils/getActiveOrg';
import getActiveRole from '../utils/getActiveRole';
// import featureAvailabilityService from './featureAvailabilityService';

export const authResultKey = 'vrpatients::auth::result';
export const productTypeKey = 'vrpatients::auth::productType';
export const groupsDataKey = 'vrpatients::auth::groupsData';
export const activeGroupsDataKey = 'vrpatients::auth::activeGroupsData';

const STUDENT_INACTIVITY_LOGOUT_DURATION = 20 * 60;

let tokenCheckInterval = null;

const webAuth = new auth0.WebAuth({
  domain: authConfig.domain,
  // redirectUri: `${window.location.origin}/auth/callback`,
  redirectUri: `${window.location.origin}/auth/callback?updated=${Date.now()}`, // prevent caching
  clientID: authConfig.clientId,
  audience: authConfig.audience,
  responseType: 'token id_token',
  scope: 'openid profile email',
});

// Support both relative and absolute users service urls
const extensionApiUrl = process.env.VUE_APP_USERS_SERVICE_URL
  ? process.env.VUE_APP_USERS_SERVICE_URL.startsWith('http')
    ? process.env.VUE_APP_USERS_SERVICE_URL + '/extension'
    : `${window.location.origin}${process.env.VUE_APP_USERS_SERVICE_URL}/extension`
  : undefined;

class AuthService extends EventEmitter {
  constructor(...args) {
    super(...args);
    this.inactivityLogoutTimer = null;
    this.sessionRenewInterval = null;
  }

  // Starts the user login flow
  login(options) {
    return new Promise((resolve, reject) => {
      webAuth.login(
        {
          ...options,
          realm: 'Username-Password-Authentication',
          extension_api_url: extensionApiUrl,
        },
        (err) => {
          if (err) {
            return reject(err);
          }
          resolve();
        }
      );
    });
  }

  signup(user) {
    return new Promise((resolve, reject) => {
      webAuth.signup(
        {
          connection: 'Username-Password-Authentication',
          ...user,
        },
        (err) => {
          if (err) return reject(err);
          resolve();
        }
      );
    });
  }

  getUser() {
    const authResult = JSON.parse(localStorage.getItem(authResultKey));
    const activeGroupsData = JSON.parse(
      localStorage.getItem(activeGroupsDataKey)
    );
    const idTokenPayload = authResult && authResult.idTokenPayload;
    const result = idTokenPayload
      ? {
          ...idTokenPayload,
          // This is for legacy purposes, some components rely on the groupsData
          // claim being present in the user prop but it has been removed due to
          // auth0 token size constraints
          'https://vrpatients.com/groupsData': activeGroupsData,
          groupsData: activeGroupsData,
          id: idTokenPayload.sub,
          user_id: idTokenPayload.sub,
          org: idTokenPayload['https://vrpatients.com/org'],
          groups: addOrgGroups(idTokenPayload['https://vrpatients.com/groups'], activeGroupsData),
          role: idTokenPayload['https://vrpatients.com/role'],
          productType: idTokenPayload['https://vrpatients.com/productType'],
          permissions: [],
        }
      : null;
    if (result) {
      result.activeOrg = getActiveOrg(result)
      result.activeRole = getActiveRole(result, result.activeOrg);
    }
    return result;
  }

  async logout() {
    await webAuth.logout({
      returnTo: window.location.origin,
    });
    localStorage.removeItem(authResultKey);
    localStorage.removeItem(productTypeKey);
    localStorage.removeItem(groupsDataKey);
    localStorage.removeItem(activeGroupsDataKey);
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
    this.emit('logout');
    // window.location.replace('/auth/login');
    window.location.replace(`/auth/login?updated=${Date.now()}`); // prevent caching
  }

  parseHashAsync() {
    return new Promise((resolve, reject) => {
      webAuth.parseHash((err, authResult) => {
        if (err) {
          reject(err);
        } else {
          resolve(authResult);
        }
      });
    });
  }

  async persistAuthResult(authResult) {
    localStorage.setItem(authResultKey, JSON.stringify(authResult));
    const productType =
      authResult.idTokenPayload['https://vrpatients.com/productType'];
    localStorage.setItem(productTypeKey, productType || 'EMS');
    const userId = authResult.idTokenPayload.sub;
    const activeOrg = getActiveOrg(authResult.idTokenPayload);
    const myGroupsResponse = await fetch(
      process.env.VUE_APP_USERS_SERVICE_URL +
        `/extension/users/${userId}/groups`,
      {
        headers: {
          Authorization: `Bearer ${authResult.idToken}`,
        },
      }
    );
    if (!myGroupsResponse.ok) {
      throw myGroupsResponse.data ||
        new Error('Error during authentication. No response from server');
    }
    const myGroups = (await myGroupsResponse.json()).map((group) => {
      try {
        return {
          ...group,
          metadata: JSON.parse(group.description),
        };
      } catch (e) {
        if (e.name === 'SyntaxError') {
          return {
            ...group,
            metadata: {},
          };
        }
        throw e;
      }
    });
    const activeGroups = myGroups.filter(
      (group) => group._id === activeOrg || group.metadata.parent === activeOrg
    );
    localStorage.setItem(groupsDataKey, JSON.stringify(myGroups));
    localStorage.setItem(activeGroupsDataKey, JSON.stringify(activeGroups));
    // featureAvailabilityService.updateUser(this.getUser());
    this.emit('sessionRenewed', authResult);
  }

  async handleAuthentication() {
    const authResult = await this.parseHashAsync();
    if (!authResult) {
      throw new Error('No authentication hash to parse');
    }
    await this.persistAuthResult(authResult);
    return authResult.idToken;
  }

  renewSession() {
    return new Promise((resolve, reject) => {
      const existingAuthResult = JSON.parse(
        localStorage.getItem(authResultKey)
      );
      if (!existingAuthResult) {
        return resolve(null);
      }
      // We don't refresh if this is a "logged in as"
      // user otherwise we will log in as the super
      // admin again
      if (existingAuthResult.fromSkeletonKey) {
        const decodedToken = jwt_decode(existingAuthResult.idToken);
        const exp = decodedToken.exp * 1000; // convert to ms
        const time = new Date().getTime();
        if (time >= exp) {
          console.log('token expired, logging out');
          auth.logout().then(() => {
            return resolve(null);
          }).catch(error => {
            return reject(error);
          });
        } else {
          return resolve(existingAuthResult);
        }
      }

      const userRole = existingAuthResult.idTokenPayload['https://vrpatients.com/role'];
      if (userRole === 'STUDENT') {
        const iat = existingAuthResult.idTokenPayload.iat;
        const tokenAge = (Date.now() / 1000) - iat;
        const sessionHasExpired = tokenAge >= STUDENT_INACTIVITY_LOGOUT_DURATION;
        if (sessionHasExpired) {
          auth.logout().then(() => {
            resolve(null);
          }).catch(error => {
            reject(error);
          });
        } else {
          this.initiateInactivityLogoutTimer(STUDENT_INACTIVITY_LOGOUT_DURATION);
        }
      }
      webAuth.checkSession({
        extension_api_url: extensionApiUrl,
      }, async (err, authResult) => {
        if (err) {
          if (window.location.hostname === 'localhost') {
            console.log('checkSession failure ignored for localhost')
          } else {
            reject(err);
            await auth.logout();
          }
        } else {
          await this.persistAuthResult(authResult);
          resolve(authResult);
          const keysToRefreshOn = [
            'https://vrpatients.com/role',
            'https://vrpatients.com/groups',
            'https://vrpatients.com/permissions',
            'https://vrpatients.com/productType',
            'https://vrpatients.com/aiActive',
            'email',
            'given_name',
            'family_name',
          ];
          if (
            !isEqual(
              pick(authResult.idTokenPayload, keysToRefreshOn),
              pick(existingAuthResult.idTokenPayload, keysToRefreshOn)
            )
          ) {
            this.emit('permissionsChanged', authResult);
          }
          this.initiateTokenCheckInterval();
        }
      });
    });
  }

  initiateInactivityLogoutTimer(durationInSeconds)  {
    const resetTimer = () => {
      clearTimeout(this.inactivityLogoutTimer);
      this.inactivityLogoutTimer = setTimeout(() => {
        auth.logout();
      }, durationInSeconds * 1000);
    };
    if (this.inactivityLogoutTimer === null) {
      resetTimer();
    }

    clearInterval(this.sessionRenewInterval);
    this.sessionRenewInterval = setInterval(() => {
      auth.renewSession();
    }, durationInSeconds * 1000);

    const activityEvents = [
      'mousedown', 'mousemove', 'keydown',
      'scroll', 'touchstart'
    ];
    activityEvents.forEach(event => {
      document.addEventListener(event, debounce(() => {
        resetTimer();
      }, 1000));
    });
  }

  initiateTokenCheckInterval() {
    if (!tokenCheckInterval) {
      tokenCheckInterval = setInterval(() => {
        if (!localStorage.getItem(authResultKey)) {
          this.logout();
        }
      }, 3000);
    }
  }

  isAuthenticated() {
    return new Promise((resolve, reject) => {
      const existingAuthResult = JSON.parse(
        localStorage.getItem(authResultKey)
      );
      if (!existingAuthResult) {
        return resolve(false);
      }
      const tokenExpiry = new Date(
        existingAuthResult.idTokenPayload.exp * 1000
      );
      if (Date.now() >= tokenExpiry) {
        webAuth.checkSession({}, (err, authResult) => {
          if (err) {
            reject(err);
          } else {
            localStorage.setItem(authResultKey, JSON.stringify(authResult));
            resolve(true);
          }
        });
      } else {
        resolve(true);
      }
    });
  }

  isEmailVerified() {
    return new Promise((resolve, reject) => {
      const existingAuthResult = JSON.parse(
        localStorage.getItem(authResultKey)
      );
      if (!existingAuthResult) {
        return false;
      }
      // If user is already logged in and email is verified
      // resolve with true
      // Otherwise, refresh the session (he might have verified
      // his email since his last login) and resolve with result
      if (existingAuthResult.idTokenPayload.email_verified) {
        resolve(true);
      } else {
        webAuth.checkSession({}, (err, authResult) => {
          if (err) {
            reject(err);
          } else {
            localStorage.setItem(authResultKey, JSON.stringify(authResult));
            resolve(authResult.idTokenPayload.email_verified);
          }
        });
      }
    });
  }

  isInOrg() {
    return new Promise((resolve, reject) => {
      const existingAuthResult = JSON.parse(
        localStorage.getItem(authResultKey)
      );
      if (!existingAuthResult) {
        return false;
      }
      // If user is already logged in and email is verified
      // resolve with true
      // Otherwise, refresh the session (he might have verified
      // his email since his last login) and resolve with result
      let groups =
        existingAuthResult.idTokenPayload['https://vrpatients.com/groups'];
      if (groups && groups[0]) {
        resolve(true);
      } else {
        webAuth.checkSession({}, (err, authResult) => {
          if (err) {
            reject(err);
          } else {
            localStorage.setItem(authResultKey, JSON.stringify(authResult));
            groups = authResult.idTokenPayload['https://vrpatients.com/groups'];
            resolve(!!(groups && groups[0]));
          }
        });
      }
    });
  }

  sendPasswordReset(email) {
    return new Promise((resolve, reject) => {
      webAuth.changePassword(
        {
          email,
          connection: 'Username-Password-Authentication',
        },
        (err) => {
          if (err) {
            return reject(err);
          }
          resolve();
        }
      );
    });
  }

  getOrgGroups() {
    const activeGroupsData = JSON.parse(localStorage.getItem(activeGroupsDataKey));
    return activeGroupsData && activeGroupsData
      .filter(g => g.metadata.parent)
      .map(g => g._id);
  }

  getOrgGroupParams() {
    return encodeURIComponent(JSON.stringify(this.getOrgGroups()));
  }
}

const auth = new AuthService();
export default auth;

function addOrgGroups(groups, groupsData) {
  for (const group of groupsData) {
    if (group.metadata.parent) {
      if (!groups.includes(group._id)) {
        groups.push(group._id)
      }
    }
  }
  return groups;
}